import moment from 'moment-timezone';
import getCalendarComparison from './getCalendarComparison';
import getCurrentDateRange from './getCurrentDateRange';
import getComparisonPeriod from './getComparisonPeriod';
import getVisSpecificComparison from './getVisSpecificComparison';
import captureException from '../../../services/captureException';
import relativeDateRangeTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';

interface BuildDateRangeProps {
  relativeDateRange?: RelativeDateRange;
  comparison?: PersistedComparisonType;
  weekStartsOn: WeekStartsOn;
  dateRange?: DateRangeInput;
  advancedRelativeDateRange?: AdvancedRelativeDateRange;
  chartDef?: VisualisationDefinition;
  isMetricListItem?: boolean;
  selectedBonusPeriod?: BonusPeriod | BonusTypes.BonusPeriod;
  bonusPeriods?: (BonusPeriod | BonusTypes.BonusPeriod)[];
  selectedPeriod?: Period;
  periods: Period[];
  timezone?: string;
  isScorecardPopupReport?: boolean;
  isUpcomingEventsBoard?: boolean;
  allFiscals?: FixedPeriods.AllFiscals;
  /**
   * A setting managed from the Advanced screen
   * - Changes the behavior of {@link RelativeDateRange}
   * - Transient on customer accounts
   * - Permanent on demo accounts
   */
  todayOverride?: string;
  /**
   * How many comparison steps back, e.g. 2 with a 'lastYear' comparison is
   * two years ago. Defaults to 1.
   */
  comparisonSteps?: number;
}

const getSingleStepDateRange = ({
  relativeDateRange,
  comparison,
  weekStartsOn,
  advancedRelativeDateRange,
  dateRange,
  chartDef,
  isMetricListItem,
  selectedBonusPeriod,
  bonusPeriods,
  selectedPeriod,
  periods,
  isScorecardPopupReport,
  isUpcomingEventsBoard,
  todayOverride,
  allFiscals,
}: BuildDateRangeProps): DateRangeInput => {
  const currentDateRange = getCurrentDateRange({
    relativeDateRange,
    weekStartsOn,
    advancedRelativeDateRange,
    dateRange,
    selectedBonusPeriod,
    selectedPeriod,
    isScorecardPopupReport,
    isUpcomingEventsBoard,
    todayOverride,
    allFiscals,
  });
  if (!currentDateRange) {
    return {};
  }
  if (!comparison) {
    return currentDateRange;
  }

  const comparisonPeriod = getComparisonPeriod({
    comparison,
    selectedBonusPeriod,
    bonusPeriods,
    selectedPeriod,
    periods,
    isScorecardPopupReport,
  });
  if (comparisonPeriod) {
    return comparisonPeriod;
  }

  if (
    relativeDateRange &&
    relativeDateRangeTypeCheckers.isFiscalInterval(relativeDateRange.interval)
  ) {
    return getCalendarComparison(
      currentDateRange,
      comparison,
      relativeDateRange ? relativeDateRange.interval : undefined,
      relativeDateRange,
      advancedRelativeDateRange,
      allFiscals,
    );
  }

  const visSpecificComparison = getVisSpecificComparison({
    relativeDateRange,
    comparison,
    chartDef,
    isMetricListItem,
    currentDateRange,
  });
  if (visSpecificComparison) {
    return visSpecificComparison;
  }
  return getCalendarComparison(
    currentDateRange,
    comparison,
    relativeDateRange ? relativeDateRange.interval : undefined,
    relativeDateRange,
    advancedRelativeDateRange,
    allFiscals,
  );
};

const getDateRange = (props: BuildDateRangeProps): DateRangeInput => {
  const firstStep = getSingleStepDateRange(props);
  const { comparison, comparisonSteps, relativeDateRange } = props;
  if (!comparison || !comparisonSteps || comparisonSteps <= 1) {
    return firstStep;
  }

  // Further steps repeat the comparison from the period the last one landed on
  let range = firstStep;
  for (let step = 1; step < comparisonSteps && range.startDate; step++) {
    range = getCalendarComparison(
      range,
      comparison,
      relativeDateRange ? relativeDateRange.interval : undefined,
      relativeDateRange,
      props.advancedRelativeDateRange,
      props.allFiscals,
    );
  }
  return range;
};

const captureTimezoneNotSet = () => {
  const e = new Error();
  e.name = 'Timezone not set for carrier';
  captureException(e);
};

const applyToLastHour = (
  dateRange: DateRangeInput,
  timezone?: string,
  isExtraDayRequired?: boolean,
): DateRangeInput => {
  if (!timezone) {
    captureTimezoneNotSet();
    return dateRange;
  }

  const { startDate, endDate, dateField } = dateRange;
  const newStart = startDate
    ? moment.tz(startDate, timezone).format('YYYY-MM-DDTHH:mm:ss')
    : undefined;
  const lastHour = moment().tz(timezone).hour();
  const newEnd = endDate
    ? moment
        .tz(endDate, timezone)
        .add({ day: isExtraDayRequired ? 1 : 0 })
        .set('hour', lastHour)
        .format('YYYY-MM-DDTHH:mm:ss')
    : undefined;
  return {
    startDate: newStart,
    endDate: newEnd,
    dateField,
  };
};

const buildDateRange = (props: BuildDateRangeProps): DateRangeInput => {
  const dateRangeResult = getDateRange(props);

  if (
    props.relativeDateRange &&
    !relativeDateRangeTypeCheckers.isFiscalInterval(
      props.relativeDateRange.interval,
    )
  ) {
    const { currentToDate, type, interval } = props.relativeDateRange;
    if (currentToDate && type !== 'next') {
      const isExtraDayRequired =
        ['week', 'month'].includes(interval) &&
        currentToDate &&
        type === 'last';
      return applyToLastHour(
        dateRangeResult,
        props.timezone,
        isExtraDayRequired,
      );
    }
  }

  return dateRangeResult;
};

export default buildDateRange;
